import React, { useEffect, useState } from "react";
import { faCircleExclamation } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import DashboardHeader from "../components/Other/DashboardHeader";
import PageHeaderTitle from "../components/pages/PageHeaderTitle";
import usePullToRefresh from "../hooks/usePullToRefresh";
import PullToRefreshIndicator from "../components/Other/PullToRefreshIndicator";
import { useTranslation } from "react-i18next";
import {
  getNotifications,
  followUnfollow,
  clearNotification,
} from "../api/user";
import { toast } from "react-toastify";
import PageContainer from "../components/pages/PageContainer";
import SettingsPageHeading from "../components/pages/SettingsPageHeading";
import EmptyState from "../components/pages/EmptyState";
import { faBell } from "@fortawesome/free-regular-svg-icons";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import {
  isLeagueNotificationsPath,
  standaloneLeaderboardPath,
} from "../utils/leaguePath";
import InfiniteScroll from "react-infinite-scroll-component";
import ListSkeleton from "../components/Skeleton/ListSkeleton";
import useDelayedFlag from "../components/Skeleton/useDelayedFlag";
import {
  groupNotificationsByBucket,
  notificationTimestamp,
} from "../utils/timeFormat";
import { baseSymbol } from "../utils/instrumentFormat";

const NotificationRow = ({
  notification,
  onOpen,
  onFollow,
  onUnfollow,
  renderImages,
}) => {
  const isAlert = notification.type === "add_notification";
  const isOrderFill = notification.type === "order_filled";
  const rawSymbol = notification?.notification_data?.stock_symbol || "";
  const symbol = baseSymbol(rawSymbol);

  // TWO-LINE STRUCTURE.
  //
  // Client (Notification System spec): "the in app Notifications feed should
  // move to a two line structure for these events: Line 1: Title, Line 2:
  // Body." The feed used to print the body alone and throw the title away, so
  // a league result read "tapeandtime is the winner of ..." with no headline,
  // and the title was a label nobody ever saw ("GAME.TITLE", "Post Liked").
  //
  // Both lines come from the backend's copy catalogue
  // (qushi-backend/lang/en/notifications.php) already rendered, which is what
  // satisfies the spec's other requirement: "for any event that appears in
  // both a push and the notifications feed, both pull from the same underlying
  // copy so they can't drift." Nothing is composed here.
  const title = notification.title;
  const body = notification.body;

  return (
    <div
      onClick={onOpen}
      className="-mx-2 flex cursor-pointer items-start gap-3 rounded-lg px-2 py-3 transition-colors hover:bg-[#F5F7F7]"
    >
      <div className="flex-shrink-0">
        {notification.title === "Unrecognized Login" ? (
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#F3F4F6]">
            <FontAwesomeIcon
              className="text-lg text-yellow-500"
              icon={faCircleExclamation}
            />
          </span>
        ) : isAlert || isOrderFill ? (
          // A price alert and an order fill are both about an instrument, not a
          // person, so the avatar slot carries the ticker rather than whichever
          // account happened to be recorded as the sender.
          <span
            className="flex h-10 w-10 items-center justify-center rounded-full bg-primaryColor font-bold tracking-tight text-white"
            style={{ fontSize: symbol.length > 4 ? "9px" : "11px" }}
          >
            {symbol}
          </span>
        ) : (
          <img
            src={notification?.sender?.image}
            alt=""
            className="h-10 w-10 rounded-full object-cover"
          />
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            {/* Line 1 — the title, carrying the weight. */}
            {title && (
              <p className="text-sm font-semibold text-gray-900">{title}</p>
            )}
            {/* Line 2 — the body. Sits under the title, one step quieter, so
                the pair reads as a headline and a detail rather than two
                sentences of equal loudness. */}
            {body && (
              <p className={`text-sm text-gray-600 ${title ? "mt-0.5" : ""}`}>
                {body}
              </p>
            )}
            {notification.notification_data?.caption && (
              <p className="mt-0.5 text-sm text-gray-600">
                {notification.notification_data?.caption}
              </p>
            )}
          </div>
          <span className="flex-shrink-0 whitespace-nowrap pt-0.5 text-xs text-secondary">
            {notificationTimestamp(notification.created_at)}
          </span>
        </div>

        {(notification.type === "friend_reqest" ||
          notification.title === "Follow") && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              const uuid = notification.notification_data?.uuid;
              if (notification.sender?.is_follow_by_me) onUnfollow(uuid);
              else onFollow(uuid);
            }}
            className="mt-2 rounded-xl border border-yellow-500 px-6 py-2 text-white hover:border-yellow-400 hover:bg-yellow-400"
            style={{ backgroundColor: "#B69D31" }}
          >
            {notification.sender?.is_follow_by_me ? "Following" : "Follow"}
          </button>
        )}

        {notification.notification_data?.post_image && (
          <div className="my-2 flex flex-wrap">
            {renderImages(notification.notification_data?.post_image)}
          </div>
        )}
      </div>
    </div>
  );
};

const Notifications = () => {
  const { t } = useTranslation();
  const [notifications, SetNotifications] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDataFeteched, setisDataFeteched] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [last_page, setlast_page] = useState();
  const [apiCall, setApiCall] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [hasMore, setHasMore] = useState(true);
  const [loader, setLoader] = useState(false);

  const handleLoadMore = () => {
    setItemsPerPage(itemsPerPage + 10);
    setPage(page + 1);
    if (page + 1 === last_page) {
      setHasMore(false);
    }
    fetchNotification(page + 1);
  };

  const fetchNotification = async (page) => {
    setLoader(true);
    try {
      if (!apiCall) {
        const response = await getNotifications(page ? page : 1);
        if (response?.data?.data?.data.length > 0) {
          SetNotifications((prevNotifications) => {
            return prevNotifications
              ? [...prevNotifications, ...response.data.data.data]
              : response.data.data.data;
          });
          if (!page || page === 1) {
            const latestId = response.data.data.data[0]?.id;
            if (latestId) {
              localStorage.setItem("lastSeenNotificationId", latestId);
            }
          }
          setApiCall(true);
          setlast_page(response.data.data.last_page);
          setLoading(false);
          setLoader(false);
          setisDataFeteched(true);
          if (page === response.data.data.last_page) {
            setHasMore(false);
          }
        } else {
          setLoader(false);
          setHasMore(false);
        }
        setLoader(false);
        setApiCall(false);
        setLoading(false);
      }
    } catch (error) {
      setLoader(false);
      setApiCall(false);
      setLoading(false);
      setHasMore(false);
      console.error("Error:", error);
    }
  };
  useEffect(() => {
    notificationClear();
    if (!isDataFeteched) fetchNotification();
    return () => {};
  }, [isDataFeteched]);

  const notificationClear = async () => {
    const data = 1;
    try {
      const response = await clearNotification(data);
      if (response.data.status == 200) {
        localStorage.setItem("notificationCount", response?.data?.data?.badge);
        const messageCount = localStorage.getItem("messageCount");
        const myCustomEvent = new CustomEvent("myCustomEvent", {
          detail: {
            messageCount: messageCount,
            notificationCount: response?.data?.data?.badge,
          },
        });
        window.dispatchEvent(myCustomEvent);
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const renderImages = (post_image) => {
    if (Array.isArray(post_image)) {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-start",
            alignItems: "center",
          }}
        >
          {post_image.map((image, index) => (
            <img
              key={index}
              className="w-12 h-12 ml-2 rounded"
              src={image.image}
              alt={`Post ${index + 1} for `}
            />
          ))}
        </div>
      );
    } else if (typeof post_image === "string") {
      return (
        <img
          className="w-12 h-12  mr-16 ml-[-10]"
          src={post_image}
          alt={`Post for ${"id"}`}
        />
      );
    }
  };

  const handleFollow = async (id) => {
    const body = {
      user_uuid: id,
      request_status: 2,
    };
    try {
      const response = await followUnfollow(body);

      if (response.status === 200) {
        fetchNotification();
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };
  const handleUnfollow = async (id) => {
    const body = {
      user_uuid: id,
      request_status: 1,
    };
    try {
      const response = await followUnfollow(body);
      if (response.status === 200) {
        fetchNotification();
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };
  const outletContext = useOutletContext();
  const sidebarToggle = Array.isArray(outletContext)
    ? outletContext[0]
    : outletContext?.sidebarToggle;

  const sections = groupNotificationsByBucket(
    (notifications || []).slice(0, itemsPerPage),
  );

  const handleSavedCharts = (chartData) => {
    const myCustomEvent = new CustomEvent("tickerEvent", {
      detail: {
        ticker: chartData?.stock_symbol,
      },
    });
    window.dispatchEvent(myCustomEvent);

    localStorage.setItem("ticker", chartData?.stock_symbol);
    localStorage.setItem("tickerEventImage", chartData?.image);
    navigate(`/charts`);
  };

  const handleImageChart = (chartData) => {
    const myCustomEvent1 = new CustomEvent("tickerEventImage", {
      detail: {
        imageChart: chartData?.image,
      },
    });
    window.dispatchEvent(myCustomEvent1);
  };

  // Where each notification takes you, per the spec's "Tap" column.
  const openNotification = (n) => {
    if (n?.type === "friend_reqest" || n?.type === "subscription") {
      // #10 New fan -> their profile.
      navigate(`/profile/${n?.send_by}`);
    } else if (n?.type === "SAVED_CHART") {
      // Legacy rows only: chart-share notifications are no longer written.
      handleImageChart(n?.notification_data?.chart);
      handleSavedCharts(n?.notification_data?.chart);
    } else if (
      n?.type === "league_completed" ||
      n?.type === "league_approved" ||
      n?.type === "game_winner" ||
      n?.type === "game_runners_up"
    ) {
      // #1-#4 -> the league's final results. #14 -> the league.
      // game_winner / game_runners_up are the pre-revamp types, kept so rows
      // already in members' feeds still open the right place.
      navigate(`/contest/${n?.notification_data?.game?.vanity_url}`);
    } else if (n?.type === "positions_closed") {
      // Account value hit $0 and was closed out. Same place the email's button
      // goes: the league's leaderboard, or The Record for the main account.
      const closedSlug = n?.notification_data?.game_vanity_url;
      navigate(closedSlug ? standaloneLeaderboardPath(closedSlug) : "/profile");
    } else if (n?.type === "order_filled") {
      // #13 -> the portfolio that was actually updated.
      //
      // Client: "I accidentally shorted Zcash in my main account, thinking i
      // was in a league account. I then realized what I did, went to the league
      // account, and shorted it again. Both notifications were identical, and
      // when I clicked them, both took me to my main QUSHi portfolio, not the
      // league portfolio."
      //
      // The backend now sends game_vanity_url alongside the fill: null for a
      // main-account trade, the competition's slug for a league or cash game.
      // Routing off that field rather than parsing redirect_url means a league
      // fill can never silently fall back to the main portfolio.
      //
      // The league portfolio lives at /contest/{slug}/portfolio. The bare
      // /{slug}/portfolio path is the PRE-REVAMP screen and is still mounted,
      // so pointing at it served the old page rather than failing loudly --
      // the client's "look where it took me". One route covers public leagues,
      // private leagues and cash games; LeaguePortfolio resolves the
      // competition from the slug and does not branch on type.
      //
      // Client: "If the trade opens a new position or adds to an existing
      // position, clicking the notification should take the user to Portfolio >
      // Holdings. If the trade fully closes a position... Portfolio > Trade
      // History." The backend decides which, because only it knows what was left
      // of the position after the fill settled; this just carries the answer.
      // Older notifications have no `portfolio_tab`, so they open the portfolio
      // on its usual default rather than a broken tab.
      const orderSlug = n?.notification_data?.game_vanity_url;
      const tab = n?.notification_data?.portfolio_tab;
      const tabQuery = tab ? `?tab=${tab}` : "";
      navigate(
        orderSlug
          ? `/contest/${orderSlug}/portfolio${tabQuery}`
          : `/portfolio${tabQuery}`
      );
    } else if (n?.type === "add_notification") {
      const stockSymbol = n?.notification_data?.stock_symbol;
      if (stockSymbol) {
        window.dispatchEvent(
          new CustomEvent("tickerEvent", { detail: { ticker: stockSymbol } }),
        );
        localStorage.setItem("ticker", stockSymbol);
        navigate(`/charts`);
      }
    } else if (n?.type === "profile_picture_reminder") {
      navigate(`/edit-profile`);
    } else {
      // Post interactions -- like, comment, reply, new post from a followed
      // trader.
      //
      // Client: "You have both the main Feed and competition feeds. If someone
      // interacts with a post that lives inside a league or game, tapping the
      // notification should open that post inside that competition, not the
      // main Feed."
      //
      // A post carries game_id, so the backend now sends the competition's slug
      // with the notification. With it the post opens inside that competition's
      // feed; without it, the main feed as before.
      const postSlug = n?.notification_data?.game_vanity_url;
      const postId = n?.notification_data?.post_id;
      navigate(postSlug ? `/${postSlug}/feeds/${postId}` : `/feed/${postId}`);
    }
  };

  const showListSkeleton = useDelayedFlag(loading);

  const hostedInLeague = isLeagueNotificationsPath(location.pathname);
  const { containerProps, isRefreshing, pullDistance } = usePullToRefresh({
    onRefresh: () => fetchNotification(1),
    disabled: false,
  });

  const body = (
    <div className="bg-white" {...containerProps}>
      <PullToRefreshIndicator
        pullDistance={pullDistance}
        isRefreshing={isRefreshing}
      />
      <main className="h-full pt-4 md:pt-10 justify-center">
        <div className="px-4 md:px-0 md:max-w-4xl w-full mx-auto mb-12">
          <SettingsPageHeading
            title={t("Notifications")}
            className="mb-3 md:mb-4"
          />
          <div className="md:rounded-2xl md:border md:border-gray-200 md:bg-white md:p-8">
            {!loading && notifications && notifications.length > 0 ? (
              <InfiniteScroll
                dataLength={notifications.length}
                next={notifications.length > 9 && handleLoadMore}
                hasMore={hasMore}
                loader={
                  loader && (
                    <ListSkeleton rows={2} avatarSize={40} columns={0} />
                  )
                }
                style={{ overflow: "hidden" }}
              >
                {sections.map(({ bucket, items }, sectionIndex) => (
                  <section
                    key={bucket}
                    className={sectionIndex === 0 ? "" : "mt-6"}
                  >
                    <h2 className="pb-1 text-sm text-secondary">{bucket}</h2>
                    {items.map((notificationD, rowIndex) => (
                      <React.Fragment key={notificationD.id}>
                        {rowIndex > 0 && (
                          <div className="ml-[52px] border-t border-divider" />
                        )}
                        <NotificationRow
                          notification={notificationD}
                          onOpen={() => openNotification(notificationD)}
                          onFollow={handleFollow}
                          onUnfollow={handleUnfollow}
                          renderImages={renderImages}
                        />
                      </React.Fragment>
                    ))}
                  </section>
                ))}
              </InfiniteScroll>
            ) : loading ? (
              showListSkeleton ? (
                <ListSkeleton rows={7} avatarSize={40} columns={0} />
              ) : null
            ) : (
              <EmptyState
                icon={faBell}
                title="No notifications yet"
                body="Likes, comments, follows, league updates, and account alerts will show up here."
              />
            )}
          </div>
        </div>
      </main>
    </div>
  );

  return (
    <>
      {!hostedInLeague && <DashboardHeader toggle={sidebarToggle} />}
      <PageHeaderTitle title={t("Notifications")} showDesktopTitle={false} />
      {hostedInLeague ? body : <PageContainer>{body}</PageContainer>}
    </>
  );
};

export default Notifications;
